import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  BarChart3,
  Gauge,
  Lock,
  MessageSquareText,
  Shield,
  ShieldCheck,
  Sparkles,
  Swords,
  Users,
  Wrench
} from 'lucide-react';
import { NavLink, Route, Routes } from 'react-router-dom';

function fetchJson<T>(url: string): Promise<T> {
  return fetch(url, { credentials: 'include' }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error ?? `Request failed: ${response.status}`);
    }
    return payload as T;
  });
}

async function submitJson<T>(url: string, method: string, body?: unknown): Promise<T> {
  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error ?? `Request failed: ${response.status}`);
  return payload as T;
}

type User = {
  id: string;
  username: string;
  tag: string;
  avatar?: string | null;
  email?: string | null;
  guilds?: Array<{ id: string; name: string; permissions: number }>;
};

type SettingsState = {
  guildId: string;
  autoRoleEnabled: boolean;
  antiSpam: boolean;
  antiCheat: boolean;
  ticketSystem: boolean;
  embedCreator: boolean;
  logChannelId: string | null;
  moderationLevel: string;
  prefix: string;
};

const navItems = [
  { label: 'Overview', to: '/dashboard', icon: BarChart3 },
  { label: 'Server Statistics', to: '/dashboard/stats', icon: Gauge },
  { label: 'Auto Role', to: '/dashboard/auto-role', icon: Shield },
  { label: 'Anti Spam', to: '/dashboard/anti-spam', icon: MessageSquareText },
  { label: 'Anti Cheat', to: '/dashboard/anti-cheat', icon: ShieldCheck },
  { label: 'Tickets', to: '/dashboard/tickets', icon: Lock },
  { label: 'Reaction Roles', to: '/dashboard/reaction-roles', icon: Sparkles },
  { label: 'Embed Creator', to: '/dashboard/embed-creator', icon: Wrench },
  { label: 'Server Status', to: '/dashboard/server-status', icon: Activity },
  { label: 'FiveGuard', to: '/dashboard/fiveguard', icon: Swords },
  { label: 'Player Lookup', to: '/dashboard/player-lookup', icon: Users },
  { label: 'Audit Logs', to: '/dashboard/logs', icon: Wrench },
  { label: 'General Settings', to: '/dashboard/settings', icon: Wrench }
];

function StatCard({ title, value, subtitle, accent }: { title: string; value: string; subtitle: string; accent: string }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 shadow-glow">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-slate-400">{title}</p>
        <span className={`h-2.5 w-2.5 rounded-full ${accent}`} />
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
      <p className="mt-2 text-sm text-slate-400">{subtitle}</p>
    </div>
  );
}

function ModulePage({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8 shadow-glow">
      <h3 className="text-2xl font-semibold text-white">{title}</h3>
      <p className="mt-3 max-w-2xl text-slate-300">{description}</p>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950/60 p-4">
      <div>
        <p className="font-medium text-white">{label}</p>
        <p className="text-sm text-slate-400">{description}</p>
      </div>
      <button
        type="button"
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${checked ? 'bg-emerald-500' : 'bg-slate-700'}`}
        onClick={() => onChange(!checked)}
        aria-pressed={checked}
      >
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${checked ? 'translate-x-5' : 'translate-x-1'}`} />
      </button>
    </label>
  );
}

function SettingsEditor({ selectedGuildId, loadPath, title, description }: { selectedGuildId: string; loadPath: string; title: string; description: string }) {
  const query = useQuery({
    queryKey: ['settings', loadPath, selectedGuildId],
    enabled: Boolean(selectedGuildId),
    queryFn: () => fetchJson<SettingsState>(`${loadPath}?guildId=${encodeURIComponent(selectedGuildId)}`)
  });

  const [state, setState] = useState<SettingsState | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  useEffect(() => {
    if (query.data) {
      setState(query.data);
    }
  }, [query.data]);

  const save = async () => {
    if (!state) {
      return;
    }

    setSaving(true);
    setSavedMessage(null);

    try {
      const response = await fetch('/api/settings', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...state, guildId: selectedGuildId })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error ?? 'Unable to save settings');
      setSavedMessage('Saved to MariaDB');
    } catch (error) {
      setSavedMessage(error instanceof Error ? error.message : 'Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModulePage title={title} description={description}>
      {query.isLoading ? (
        <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-slate-300">Loading settings...</div>
      ) : query.isError ? (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-red-200">Unable to load real settings from the backend.</div>
      ) : state ? (
        <div className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <label className="rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-sm text-slate-300">
              Guild ID
              <input
                value={state.guildId}
                readOnly
                className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white"
              />
            </label>
            <label className="rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-sm text-slate-300">
              Log channel ID
              <input
                value={state.logChannelId ?? ''}
                onChange={(event) => setState({ ...state, logChannelId: event.target.value || null })}
                placeholder="Channel ID"
                className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white"
              />
            </label>
          </div>

          <ToggleRow
            label="Auto role enabled"
            description="Assign configured roles to new members automatically."
            checked={state.autoRoleEnabled}
            onChange={(checked) => setState({ ...state, autoRoleEnabled: checked })}
          />
          <ToggleRow
            label="Anti-spam enabled"
            description="Timeout users and remove recent spam-like messages when enabled."
            checked={state.antiSpam}
            onChange={(checked) => setState({ ...state, antiSpam: checked })}
          />
          <ToggleRow
            label="Anti-cheat protections enabled"
            description="Apply protective checks for risky players and moderation workflows."
            checked={state.antiCheat}
            onChange={(checked) => setState({ ...state, antiCheat: checked })}
          />
          <ToggleRow
            label="Ticket system enabled"
            description="Allow support ticket creation and handling."
            checked={state.ticketSystem}
            onChange={(checked) => setState({ ...state, ticketSystem: checked })}
          />
          <ToggleRow
            label="Embed creator enabled"
            description="Allow staff to build and send colored embeds through the bot."
            checked={state.embedCreator}
            onChange={(checked) => setState({ ...state, embedCreator: checked })}
          />

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between rounded-xl border border-slate-800 bg-slate-950/60 p-4">
            <div>
              <p className="font-medium text-white">Command prefix</p>
              <p className="text-sm text-slate-400">Prefix used for legacy bot commands.</p>
            </div>
            <input
              className="w-20 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-center text-white"
              value={state.prefix}
              onChange={(event) => setState({ ...state, prefix: event.target.value })}
            />
          </div>

          <div className="flex items-center justify-between gap-4 pt-2">
            <div className="text-sm text-slate-300">{savedMessage ?? 'Changes are not applied until you save.'}</div>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="rounded-xl bg-blue-500 px-4 py-2 font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      ) : null}
    </ModulePage>
  );
}

const fieldClass = 'mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white';
const buttonClass = 'rounded-lg bg-blue-500 px-4 py-2 font-medium text-white disabled:opacity-50';

function GuildOptions({ query, kind }: { query: ReturnType<typeof useQuery<{ channels: Array<{ id: string; name: string }>; categories: Array<{ id: string; name: string }> }>>; kind: 'channels' | 'categories' }) {
  const items = query.data?.[kind] ?? [];
  return <>{items.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</>;
}

function AutorolePage({ guildId }: { guildId: string }) {
  const client = useQueryClient();
  const rolesQuery = useQuery<{ roles: Array<{ id: string; roleId: string; enabled: boolean }> }>({
    queryKey: ['autoroles', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/autoroles?guildId=${encodeURIComponent(guildId)}`)
  });
  const discordRoles = useQuery<{ roles: Array<{ id: string; name: string }> }>({
    queryKey: ['discord-roles', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/roles?guildId=${encodeURIComponent(guildId)}`)
  });
  const [roleId, setRoleId] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      await submitJson('/api/autoroles', 'POST', { guildId, roleId, enabled: true });
      await client.invalidateQueries({ queryKey: ['autoroles', guildId] });
      setMessage('Autorole saved.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to save autorole');
    } finally { setBusy(false); }
  };
  const remove = async (id: string) => {
    setBusy(true);
    setMessage('');
    try {
      await submitJson(`/api/autoroles/${encodeURIComponent(id)}?guildId=${encodeURIComponent(guildId)}`, 'DELETE');
      await client.invalidateQueries({ queryKey: ['autoroles', guildId] });
      setMessage('Autorole removed.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to remove autorole'); }
    finally { setBusy(false); }
  };
  return <ModulePage title="Auto Role" description="Configure roles assigned to new members. The bot validates role hierarchy and has a /autorole repair command for existing members.">
    {rolesQuery.isLoading || discordRoles.isLoading ? <p>Loading roles...</p> : rolesQuery.isError || discordRoles.isError ? <p className="text-red-300">Unable to load roles from Origin or Discord.</p> : null}
    <form onSubmit={save} className="flex flex-col gap-3 md:flex-row">
      <select className={fieldClass} value={roleId} onChange={(event) => setRoleId(event.target.value)} required>
        <option value="">Choose a Discord role</option>
        {discordRoles.data?.roles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
      </select>
      <button className={buttonClass} disabled={busy || !roleId}>{busy ? 'Saving...' : 'Add role'}</button>
    </form>
    {message && <p className="mt-3 text-sm text-slate-300">{message}</p>}
    <div className="mt-5 space-y-2">
      {(rolesQuery.data?.roles ?? []).map((role) => <div key={role.id} className="flex items-center justify-between rounded-lg border border-slate-800 p-3">
        <span className="text-white">&lt;@&amp;{role.roleId}&gt; {role.enabled ? '' : '(disabled)'}</span>
        <button className="rounded border border-red-500/50 px-3 py-1 text-red-200" disabled={busy} onClick={() => void remove(role.id)}>Remove</button>
      </div>)}
      {!rolesQuery.isLoading && (rolesQuery.data?.roles.length ?? 0) === 0 && <p className="text-slate-400">No autoroles configured.</p>}
    </div>
  </ModulePage>;
}

function AntiSpamPage({ guildId }: { guildId: string }) {
  const client = useQueryClient();
  const channels = useQuery<{ channels: Array<{ id: string; name: string }> }>({
    queryKey: ['discord-channels', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/channels?guildId=${encodeURIComponent(guildId)}`)
  });
  const query = useQuery<{ enabled: boolean; configChannelId: string | null }>({
    queryKey: ['anti-spam', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/anti-spam?guildId=${encodeURIComponent(guildId)}`)
  });
  const logs = useQuery<{ logs: Array<{ id: string; userId: string; action: string; reason: string | null; createdAt: string }> }>({
    queryKey: ['anti-spam-logs', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/anti-spam/logs?guildId=${encodeURIComponent(guildId)}`)
  });
  const [enabled, setEnabled] = useState(true);
  const [channelId, setChannelId] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (query.data) { setEnabled(query.data.enabled); setChannelId(query.data.configChannelId ?? ''); }
  }, [query.data]);
  const save = async () => {
    setBusy(true); setMessage('');
    try {
      await submitJson('/api/anti-spam', 'POST', { guildId, enabled, configChannelId: channelId || null });
      await client.invalidateQueries({ queryKey: ['anti-spam', guildId] });
      setMessage('Anti-spam settings saved. Posts in the selected channel trigger a 24-hour timeout.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to save anti-spam settings'); }
    finally { setBusy(false); }
  };
  return <ModulePage title="Anti Spam" description="Messages posted in the selected channel are removed from the preceding hour and the author is timed out for 24 hours. Ticket channels are excluded.">
    {query.isLoading || channels.isLoading ? <p>Loading settings...</p> : null}
    {query.isError || channels.isError ? <p className="text-red-300">Unable to load anti-spam or Discord channel settings.</p> : null}
    <label className="flex items-center gap-3 text-white"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />Enabled</label>
    <label className="mt-4 block text-sm text-slate-300">Monitored channel
      <select className={fieldClass} value={channelId} onChange={(event) => setChannelId(event.target.value)}>
        <option value="">Choose a channel</option>{channels.data?.channels.map((channel) => <option key={channel.id} value={channel.id}>#{channel.name}</option>)}
      </select>
    </label>
    <button className={`${buttonClass} mt-4`} disabled={busy || (enabled && !channelId)} onClick={() => void save()}>{busy ? 'Saving...' : 'Save anti-spam settings'}</button>
    {message && <p className="mt-3 text-sm text-slate-300">{message}</p>}
    {logs.isError && <p className="mt-3 text-red-300">Unable to load moderation logs.</p>}
    <h4 className="mt-7 font-semibold text-white">Recent enforcement</h4>
    <div className="mt-2 space-y-2">{(logs.data?.logs ?? []).map((log) => <p key={log.id} className="rounded-lg border border-slate-800 p-3 text-sm">{log.action} · {log.userId} · {new Date(log.createdAt).toLocaleString()} · {log.reason}</p>)}</div>
  </ModulePage>;
}

function TicketsPage({ guildId }: { guildId: string }) {
  const client = useQueryClient();
  const resources = useQuery<{ channels: Array<{ id: string; name: string }>; categories: Array<{ id: string; name: string }> }>({
    queryKey: ['discord-channels', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/channels?guildId=${encodeURIComponent(guildId)}`)
  });
  const roles = useQuery<{ roles: Array<{ id: string; name: string }> }>({
    queryKey: ['discord-roles', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/roles?guildId=${encodeURIComponent(guildId)}`)
  });
  const panels = useQuery<{ panels: Array<{ id: string; title: string; channelId: string; messageId: string | null; enabled: boolean }> }>({
    queryKey: ['ticket-panels', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/ticket-panels?guildId=${encodeURIComponent(guildId)}`)
  });
  const tickets = useQuery<{ tickets: Array<{ id: string; title: string; status: string; creatorName: string; createdAt: string; closedAt: string | null }> }>({
    queryKey: ['tickets', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/tickets?guildId=${encodeURIComponent(guildId)}`)
  });
  const [form, setForm] = useState({ title: 'Support', description: '', channelId: '', categoryId: '', supportRoleIds: [] as string[], buttonLabel: 'Create ticket', buttonStyle: 'primary', color: '#5865F2' });
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const createPanel = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setMessage('');
    try {
      await submitJson('/api/ticket-panels', 'POST', { ...form, guildId });
      await client.invalidateQueries({ queryKey: ['ticket-panels', guildId] });
      setMessage('Panel published by the Discord bot.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to publish ticket panel'); }
    finally { setBusy(false); }
  };
  const togglePanel = async (id: string, enabled: boolean) => {
    try {
      await submitJson(`/api/ticket-panels/${encodeURIComponent(id)}?guildId=${encodeURIComponent(guildId)}`, 'PATCH', { enabled: !enabled });
      await client.invalidateQueries({ queryKey: ['ticket-panels', guildId] });
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to update ticket panel'); }
  };
  return <ModulePage title="Tickets" description="Publish ticket panels to Discord, then manage active tickets through the ticket channel buttons. Closed tickets keep transcripts and history in MariaDB.">
    <form className="grid gap-3 md:grid-cols-2" onSubmit={(event) => void createPanel(event)}>
      <input className={fieldClass} value={form.title} maxLength={100} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="Panel title" required />
      <label className="text-sm text-slate-300">Embed color<input className={`${fieldClass} h-10 p-1`} type="color" value={form.color} onChange={(event) => setForm({ ...form, color: event.target.value })} /></label>
      <select className={fieldClass} value={form.channelId} onChange={(event) => setForm({ ...form, channelId: event.target.value })} required><option value="">Publish in channel</option><GuildOptions query={resources} kind="channels" /></select>
      <select className={fieldClass} value={form.categoryId} onChange={(event) => setForm({ ...form, categoryId: event.target.value })}><option value="">No ticket category</option><GuildOptions query={resources} kind="categories" /></select>
      <select multiple className={fieldClass} value={form.supportRoleIds} onChange={(event) => setForm({ ...form, supportRoleIds: Array.from(event.target.selectedOptions, (option) => option.value) })} aria-label="Support roles">{roles.data?.roles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}</select>
      <input className={fieldClass} value={form.buttonLabel} maxLength={80} onChange={(event) => setForm({ ...form, buttonLabel: event.target.value })} placeholder="Button label" required />
      <select className={fieldClass} value={form.buttonStyle} onChange={(event) => setForm({ ...form, buttonStyle: event.target.value })}><option value="primary">Primary button</option><option value="secondary">Secondary button</option><option value="success">Success button</option><option value="danger">Danger button</option></select>
      <textarea className={`${fieldClass} md:col-span-2`} value={form.description} maxLength={4000} onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder="Panel description" />
      <button className={buttonClass} disabled={busy}>{busy ? 'Publishing...' : 'Publish ticket panel'}</button>
    </form>
    {message && <p className="mt-3 text-sm text-slate-300">{message}</p>}
    {resources.isError || roles.isError || panels.isError || tickets.isError ? <p className="mt-3 text-red-300">Unable to load ticket or Discord configuration.</p> : null}
    <h4 className="mt-7 font-semibold text-white">Panels</h4>
    <div className="mt-2 space-y-2">{(panels.data?.panels ?? []).map((panel) => <div key={panel.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800 p-3"><span>{panel.title} · channel {panel.channelId} · message {panel.messageId ?? 'pending'} · {panel.enabled ? 'enabled' : 'disabled'}</span><button className="rounded border border-slate-700 px-3 py-1 text-white" onClick={() => void togglePanel(panel.id, panel.enabled)}>{panel.enabled ? 'Disable' : 'Enable'}</button></div>)}</div>
    <h4 className="mt-7 font-semibold text-white">Ticket history</h4>
    <div className="mt-2 space-y-2">{(tickets.data?.tickets ?? []).map((ticket) => <div key={ticket.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800 p-3">
      <span>{ticket.title} · {ticket.creatorName} · {ticket.status}</span>
      <span className="flex items-center gap-3 text-sm text-slate-400">{new Date(ticket.createdAt).toLocaleString()}{ticket.closedAt && <a className="text-blue-300 underline" href={`/api/tickets/${encodeURIComponent(ticket.id)}/transcript`} target="_blank" rel="noreferrer">Transcript</a>}</span>
    </div>)}</div>
  </ModulePage>;
}

function ReactionRolesPage({ guildId }: { guildId: string }) {
  const client = useQueryClient();
  const resources = useQuery<{ channels: Array<{ id: string; name: string }> }>({
    queryKey: ['discord-channels', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/channels?guildId=${encodeURIComponent(guildId)}`)
  });
  const roles = useQuery<{ roles: Array<{ id: string; name: string }> }>({
    queryKey: ['discord-roles', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/roles?guildId=${encodeURIComponent(guildId)}`)
  });
  const panels = useQuery<{ panels: Array<{ id: string; title: string; messageId: string | null; enabled: boolean; mappings: Array<{ emoji: string; roleId: string }> }> }>({
    queryKey: ['reaction-roles', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/reaction-roles?guildId=${encodeURIComponent(guildId)}`)
  });
  const [form, setForm] = useState({ title: 'Choose your roles', channelId: '' });
  const [mappingRows, setMappingRows] = useState([{ emoji: '', roleId: '' }]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setMessage('');
    try {
      await submitJson('/api/reaction-roles', 'POST', { guildId, ...form, mappings: mappingRows });
      await client.invalidateQueries({ queryKey: ['reaction-roles', guildId] });
      setMessage('Reaction role panel published and persisted.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to create reaction role panel'); }
    finally { setBusy(false); }
  };
  const togglePanel = async (id: string, enabled: boolean) => {
    try {
      await submitJson(`/api/reaction-roles/${encodeURIComponent(id)}?guildId=${encodeURIComponent(guildId)}`, 'PATCH', { enabled: !enabled });
      await client.invalidateQueries({ queryKey: ['reaction-roles', guildId] });
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to update reaction role panel'); }
  };
  return <ModulePage title="Reaction Roles" description="Create reaction panels using the bot. Mappings persist and role hierarchy is checked before members receive roles.">
    <form className="grid gap-3 md:grid-cols-2" onSubmit={(event) => void save(event)}>
      <input className={fieldClass} value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="Panel title" required />
      <select className={fieldClass} value={form.channelId} onChange={(event) => setForm({ ...form, channelId: event.target.value })} required><option value="">Panel channel</option>{resources.data?.channels.map((channel) => <option key={channel.id} value={channel.id}>#{channel.name}</option>)}</select>
      {mappingRows.map((mapping, index) => <div key={index} className="grid gap-3 md:col-span-2 md:grid-cols-[1fr_1fr_auto]">
        <input className={fieldClass} value={mapping.emoji} onChange={(event) => setMappingRows(mappingRows.map((row, i) => i === index ? { ...row, emoji: event.target.value } : row))} placeholder="Emoji (Unicode or custom emoji)" required />
        <select className={fieldClass} value={mapping.roleId} onChange={(event) => setMappingRows(mappingRows.map((row, i) => i === index ? { ...row, roleId: event.target.value } : row))} required><option value="">Role to assign</option>{roles.data?.roles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}</select>
        <button type="button" className="rounded border border-red-500/50 px-3 text-red-200 disabled:opacity-40" disabled={mappingRows.length === 1} onClick={() => setMappingRows(mappingRows.filter((_, i) => i !== index))}>Remove</button>
      </div>)}
      <div className="flex flex-wrap gap-2 md:col-span-2">
        <button type="button" className="rounded border border-slate-700 px-3 py-2 text-white" disabled={mappingRows.length >= 20} onClick={() => setMappingRows([...mappingRows, { emoji: '', roleId: '' }])}>Add mapping</button>
        <button className={buttonClass} disabled={busy}>{busy ? 'Publishing...' : 'Create panel'}</button>
      </div>
    </form>
    {message && <p className="mt-3 text-sm text-slate-300">{message}</p>}
    {resources.isError || roles.isError || panels.isError ? <p className="mt-3 text-red-300">Unable to load Discord reaction role settings.</p> : null}
    <div className="mt-6 space-y-2">{(panels.data?.panels ?? []).map((panel) => <div key={panel.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800 p-3"><span><strong>{panel.title}</strong> · {panel.mappings.map((mapping) => `${mapping.emoji} → ${mapping.roleId}`).join(', ')} · message {panel.messageId ?? 'unpublished'} · {panel.enabled ? 'enabled' : 'disabled'}</span><button className="rounded border border-slate-700 px-3 py-1 text-white" onClick={() => void togglePanel(panel.id, panel.enabled)}>{panel.enabled ? 'Disable' : 'Enable'}</button></div>)}</div>
  </ModulePage>;
}

function EmbedCreatorPage({ guildId }: { guildId: string }) {
  const client = useQueryClient();
  const channels = useQuery<{ channels: Array<{ id: string; name: string }> }>({
    queryKey: ['discord-channels', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/channels?guildId=${encodeURIComponent(guildId)}`)
  });
  const templates = useQuery<{ templates: Array<{ id: string; name: string; payload: unknown }> }>({
    queryKey: ['embed-templates', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/embed-templates?guildId=${encodeURIComponent(guildId)}`)
  });
  const initialPayload = JSON.stringify({ embeds: [{ title: 'Announcement', description: 'Write your message here.', color: '#5865F2', author: '', footer: '', timestamp: true, thumbnailUrl: '', imageUrl: '', fields: [] }], buttons: [] }, null, 2);
  const [payload, setPayload] = useState(initialPayload);
  const [templateName, setTemplateName] = useState('');
  const [channelId, setChannelId] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  let preview: any = null;
  try { preview = JSON.parse(payload); } catch { preview = null; }
  const payloadObject = () => {
    const parsed: unknown = JSON.parse(payload);
    if (!parsed || typeof parsed !== 'object') throw new Error('Editor content must be a JSON object');
    return parsed;
  };
  const saveTemplate = async () => {
    setBusy(true); setMessage('');
    try {
      await submitJson('/api/embed-templates', 'POST', { guildId, name: templateName, payload: payloadObject() });
      await client.invalidateQueries({ queryKey: ['embed-templates', guildId] });
      setMessage('Template saved to MariaDB.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to save template'); }
    finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true); setMessage('');
    try {
      await submitJson('/api/embeds/send', 'POST', { guildId, channelId, payload: payloadObject() });
      setMessage('Message sent through the Discord bot.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to send embed'); }
    finally { setBusy(false); }
  };
  return <ModulePage title="Embed Creator" description="Edit full Discord embed JSON with a live preview, save templates to MariaDB, and send messages through the Discord bot. Interactive buttons reply to their clicker; link buttons use native Discord links.">
    <div className="grid gap-5 xl:grid-cols-2">
      <div>
        <textarea className={`${fieldClass} min-h-80 font-mono text-sm`} value={payload} onChange={(event) => setPayload(event.target.value)} spellCheck={false} aria-label="Embed JSON editor" />
        <label className="mt-3 block text-sm">Template name<input className={fieldClass} value={templateName} onChange={(event) => setTemplateName(event.target.value)} /></label>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className={buttonClass} disabled={busy || !templateName.trim()} onClick={() => void saveTemplate()}>Save template</button>
          <select className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white" value="" onChange={(event) => {
            const template = templates.data?.templates.find((item) => item.id === event.target.value);
            if (template) { setTemplateName(template.name); setPayload(JSON.stringify(template.payload, null, 2)); }
          }}><option value="">Load template</option>{templates.data?.templates.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
        </div>
        <div className="mt-3 flex gap-2">
          <select className={fieldClass} value={channelId} onChange={(event) => setChannelId(event.target.value)}><option value="">Send to channel</option>{channels.data?.channels.map((channel) => <option key={channel.id} value={channel.id}>#{channel.name}</option>)}</select>
          <button type="button" className={buttonClass} disabled={busy || !channelId} onClick={() => void send()}>{busy ? 'Working...' : 'Send'}</button>
        </div>
        {message && <p className="mt-3 text-sm text-slate-300">{message}</p>}
        {templates.isError || channels.isError ? <p className="mt-3 text-red-300">Unable to load templates or Discord channels.</p> : null}
      </div>
      <div className="space-y-3">
        {!preview ? <p className="text-red-300">Invalid JSON; preview and send are unavailable.</p> : (preview.embeds ?? [preview]).map((embed: any, index: number) => <div key={index} className="rounded-xl border-l-4 border-blue-500 bg-slate-950 p-4" style={{ borderColor: typeof embed.color === 'string' ? embed.color : '#5865F2' }}>
          {embed.author && <p className="text-xs text-slate-400">{typeof embed.author === 'string' ? embed.author : embed.author.name}</p>}
          {embed.title && <h4 className="mt-1 text-lg font-semibold text-white">{embed.title}</h4>}
          {embed.description && <p className="mt-2 whitespace-pre-wrap text-slate-300">{embed.description}</p>}
          {Array.isArray(embed.fields) && <div className="mt-3 grid gap-2 sm:grid-cols-2">{embed.fields.map((field: any, i: number) => <div key={i}><strong>{field.name}</strong><p className="text-slate-300">{field.value}</p></div>)}</div>}
          {embed.imageUrl && <img className="mt-3 max-h-56 rounded object-contain" src={embed.imageUrl} alt="Embed preview" />}
          {embed.thumbnailUrl && <img className="mt-3 h-16 w-16 rounded object-cover" src={embed.thumbnailUrl} alt="Embed thumbnail preview" />}
          {embed.footer && <p className="mt-3 text-xs text-slate-400">{typeof embed.footer === 'string' ? embed.footer : embed.footer.text}</p>}
        </div>)}
        {(preview?.buttons ?? []).map((button: any, index: number) => <span key={index} className="mr-2 inline-block rounded bg-blue-600 px-3 py-2 text-white">{button.label} · {button.style}</span>)}
      </div>
    </div>
  </ModulePage>;
}

function FiveMStatusPage({ guildId }: { guildId: string }) {
  const client = useQueryClient();
  const channels = useQuery<{ channels: Array<{ id: string; name: string }> }>({
    queryKey: ['discord-channels', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/channels?guildId=${encodeURIComponent(guildId)}`)
  });
  const query = useQuery<{ serverIp: string; serverPort: string; updateInterval: string; statusChannelId: string; statusMessage: string }>({
    queryKey: ['fivem-config', guildId],
    enabled: Boolean(guildId),
    queryFn: () => fetchJson(`/api/fivem/status/config?guildId=${encodeURIComponent(guildId)}`)
  });
  const status = useQuery<{ online: boolean; playerCount: number; maxPlayers: number; serverName: string; lastUpdated: string | null; error?: string }>({
    queryKey: ['fivem-status'],
    queryFn: () => fetchJson('/api/fivem/status?refresh=1'),
    refetchInterval: 30000
  });
  const [form, setForm] = useState({ serverIp: '', serverPort: '30120', updateInterval: '30000', statusChannelId: '', statusMessage: '' });
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (query.data) setForm(query.data); }, [query.data]);
  const save = async () => {
    setBusy(true); setMessage('');
    try {
      await submitJson('/api/fivem/status', 'POST', { ...form, guildId });
      await client.invalidateQueries({ queryKey: ['fivem-config', guildId] });
      setMessage('FiveM polling and Discord status-message settings saved.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to save FiveM configuration'); }
    finally { setBusy(false); }
  };
  return <ModulePage title="FiveM Server Status" description="Polls dynamic.json, players.json, and info.json with HTTP timeouts, then updates the database snapshot and configured Discord status message.">
    <div className="mb-5 grid gap-3 md:grid-cols-4">
      <div className="rounded-lg border border-slate-800 p-3"><p className="text-slate-400">Status</p><p className="text-xl">{status.data?.online ? 'Online' : status.data ? 'Offline' : 'Not configured'}</p></div>
      <div className="rounded-lg border border-slate-800 p-3"><p className="text-slate-400">Players</p><p className="text-xl">{status.data ? `${status.data.playerCount}/${status.data.maxPlayers}` : '—'}</p></div>
      <div className="rounded-lg border border-slate-800 p-3"><p className="text-slate-400">Server</p><p className="text-xl">{status.data?.serverName ?? '—'}</p></div>
      <div className="rounded-lg border border-slate-800 p-3"><p className="text-slate-400">Last updated</p><p className="text-sm">{status.data?.lastUpdated ? new Date(status.data.lastUpdated).toLocaleString() : '—'}</p></div>
    </div>
    {(query.isError || channels.isError) && <p className="mb-3 text-red-300">Unable to load FiveM settings or Discord channels.</p>}
    {status.data?.error && <p className="mb-3 text-amber-300">{status.data.error}</p>}
    <div className="grid gap-3 md:grid-cols-2">
      <label className="text-sm text-slate-300">Server host<input className={fieldClass} value={form.serverIp} onChange={(event) => setForm({ ...form, serverIp: event.target.value })} placeholder="example.com" /></label>
      <label className="text-sm text-slate-300">Port<input className={fieldClass} type="number" min="1" max="65535" value={form.serverPort} onChange={(event) => setForm({ ...form, serverPort: event.target.value })} /></label>
      <label className="text-sm text-slate-300">Update interval (ms)<input className={fieldClass} type="number" min="15000" max="3600000" step="1000" value={form.updateInterval} onChange={(event) => setForm({ ...form, updateInterval: event.target.value })} /></label>
      <label className="text-sm text-slate-300">Discord status channel<select className={fieldClass} value={form.statusChannelId} onChange={(event) => setForm({ ...form, statusChannelId: event.target.value })}><option value="">No status message</option>{channels.data?.channels.map((channel) => <option key={channel.id} value={channel.id}>#{channel.name}</option>)}</select></label>
      <label className="text-sm text-slate-300 md:col-span-2">Status message text<input className={fieldClass} value={form.statusMessage} maxLength={1000} onChange={(event) => setForm({ ...form, statusMessage: event.target.value })} /></label>
    </div>
    <button className={`${buttonClass} mt-4`} disabled={busy} onClick={() => void save()}>{busy ? 'Saving...' : 'Save FiveM settings'}</button>
    {message && <p className="mt-3 text-sm text-slate-300">{message}</p>}
  </ModulePage>;
}

function LoginScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4">
      <div className="w-full max-w-md rounded-3xl border border-slate-800 bg-slate-900/80 p-8 shadow-glow backdrop-blur-xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/20 text-xl font-bold text-blue-300">O</div>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Origin</p>
            <h1 className="text-xl font-semibold text-white">Dashboard</h1>
          </div>
        </div>

        <h2 className="text-3xl font-bold text-white">Secure access</h2>
        <p className="mt-3 text-sm leading-6 text-slate-300">
          Log in with Discord to manage your guilds, moderation workflows, and live server status.
        </p>

        <a
          href="/auth/discord"
          className="mt-8 flex w-full items-center justify-center gap-3 rounded-xl bg-[#5865F2] px-4 py-3 text-base font-medium text-white transition hover:bg-[#4752c4]"
        >
          <span>Continue with Discord</span>
        </a>
      </div>
    </div>
  );
}

function DashboardShell({ user }: { user: User }) {
  const defaultGuildId = user.guilds?.[0]?.id ?? '';
  const [selectedGuildId, setSelectedGuildId] = useState(defaultGuildId);

  useEffect(() => {
    setSelectedGuildId(defaultGuildId);
  }, [defaultGuildId]);

  const overview = useQuery({
    queryKey: ['overview'],
    queryFn: () => fetchJson<{ totalGuilds: number; totalUsers: number; stats: Record<string, number>; totalTickets: number; openTickets: number }>('/api/overview')
  });

  const servers = useQuery({
    queryKey: ['servers'],
    queryFn: () => fetchJson<{ guilds: Array<{ id: string; name: string; permissions: number }> }>('/api/servers')
  });

  const status = useQuery({
    queryKey: ['status'],
    queryFn: () => fetchJson<{ connected: boolean; latency: number; version: string; region: string; lastHeartbeat: string }>('/api/status')
  });

  const auditLogs = useQuery({
    queryKey: ['logs'],
    queryFn: () => fetchJson<{ logs: Array<{ id: string; type: string; message: string; time: string }> }>('/api/logs')
  });

  const stats = useMemo(
    () => [
      { title: 'Guilds', value: String(overview.data?.totalGuilds ?? 0), subtitle: 'Managed communities', accent: 'bg-emerald-400' },
      { title: 'Users', value: String(overview.data?.totalUsers ?? 0), subtitle: 'Discord members tracked', accent: 'bg-blue-400' },
      { title: 'Moderation', value: String(overview.data?.stats.moderationEvents ?? 0), subtitle: 'Audit events recorded', accent: 'bg-violet-400' },
      { title: 'Players', value: String(overview.data?.stats.playersOnline ?? 0), subtitle: 'FiveM players seen', accent: 'bg-cyan-400' }
    ],
    [overview.data]
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <aside className="fixed left-0 top-0 hidden h-screen w-72 border-r border-slate-800 bg-slate-900/80 p-5 lg:block">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/20 font-bold text-blue-300">O</div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.32em] text-slate-400">Origin</p>
            <h1 className="text-lg font-semibold text-white">Control Center</h1>
          </div>
        </div>

        <nav className="space-y-2">
          {navItems.map(({ label, to, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition ${
                  isActive ? 'bg-blue-500/20 text-blue-200' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="lg:pl-72">
        <header className="sticky top-0 z-20 border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl">
          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <p className="text-xs uppercase tracking-[0.32em] text-slate-400">Operational dashboard</p>
              <h2 className="text-xl font-semibold text-white">ORIGIN</h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-sm text-emerald-300">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                {status.data?.connected ? 'Bot online' : 'Bot offline'}
              </div>
              <button
                className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white"
                onClick={() => {
                  fetch('/auth/logout', { method: 'POST', credentials: 'include' }).then(() => window.location.reload());
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        <main className="space-y-6 p-5 md:p-7">
          {user.guilds && user.guilds.length > 0 ? (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
              <label className="flex items-center gap-3 text-sm text-slate-300">
                <span>Guild</span>
                <select
                  value={selectedGuildId}
                  onChange={(event) => setSelectedGuildId(event.target.value)}
                  className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
                >
                  {user.guilds.map((guild) => (
                    <option key={guild.id} value={guild.id}>
                      {guild.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}

          <Routes>
            <Route
              path="/dashboard"
              element={
                <>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    {stats.map((stat) => (
                      <StatCard key={stat.title} {...stat} />
                    ))}
                  </div>

                  <div className="mt-6 grid gap-6 xl:grid-cols-[1.5fr_1fr]">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 shadow-glow">
                      <div className="mb-4 flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-white">Guild access</h3>
                        <span className="text-sm text-slate-400">{servers.data?.guilds.length ?? 0} servers</span>
                      </div>
                      <div className="space-y-3">
                        {(servers.data?.guilds ?? []).map((guild) => (
                          <div key={guild.id} className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950/60 p-3">
                            <div>
                              <p className="font-medium text-white">{guild.name}</p>
                              <p className="text-xs text-slate-400">{guild.permissions ? 'Manage permissions granted' : 'Limited access'}</p>
                            </div>
                            <span className="rounded-full bg-slate-800 px-2 py-1 text-xs text-slate-300">
                              {guild.permissions & 0x20 ? 'Admin' : 'Moderator'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 shadow-glow">
                      <h3 className="text-lg font-semibold text-white">Account</h3>
                      <div className="mt-4 flex items-center gap-4">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-500/20 text-lg font-bold text-blue-300">
                          {user.username.slice(0, 1).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-white">{user.username}</p>
                          <p className="text-sm text-slate-400">{user.tag}</p>
                        </div>
                      </div>
                      <div className="mt-6 space-y-3 text-sm text-slate-300">
                        <p>Discord OAuth enabled</p>
                        <p>Session protection active</p>
                        <p>Dashboard permissions validated</p>
                      </div>
                    </div>
                  </div>
                </>
              }
            />
            <Route
              path="/dashboard/stats"
              element={
                <ModulePage title="Server Statistics" description="Real backend health and bot status are loaded from the Origin API.">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                      <p className="text-sm text-slate-400">Connection status</p>
                      <p className="mt-2 text-2xl font-semibold text-white">{status.data?.connected ? 'Connected' : 'Disconnected'}</p>
                    </div>
                    <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                      <p className="text-sm text-slate-400">Discord latency</p>
                      <p className="mt-2 text-2xl font-semibold text-white">{status.data?.latency ?? 0}ms</p>
                    </div>
                    <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                      <p className="text-sm text-slate-400">Open tickets</p>
                      <p className="mt-2 text-2xl font-semibold text-white">{overview.data?.openTickets ?? 0}</p>
                    </div>
                    <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                      <p className="text-sm text-slate-400">Registered players</p>
                      <p className="mt-2 text-2xl font-semibold text-white">{overview.data?.playersOnline ?? 0}</p>
                    </div>
                  </div>
                </ModulePage>
              }
            />
            <Route path="/dashboard/auto-role" element={<AutorolePage guildId={selectedGuildId} />} />
            <Route path="/dashboard/anti-spam" element={<AntiSpamPage guildId={selectedGuildId} />} />
            <Route path="/dashboard/anti-cheat" element={<SettingsEditor selectedGuildId={selectedGuildId} loadPath="/api/settings" title="Anti Cheat" description="Review global moderation protections and save them through the backend settings API." />} />
            <Route path="/dashboard/tickets" element={<TicketsPage guildId={selectedGuildId} />} />
            <Route path="/dashboard/reaction-roles" element={<ReactionRolesPage guildId={selectedGuildId} />} />
            <Route path="/dashboard/embed-creator" element={<EmbedCreatorPage guildId={selectedGuildId} />} />
            <Route path="/dashboard/server-status" element={<FiveMStatusPage guildId={selectedGuildId} />} />
            <Route path="/dashboard/fiveguard" element={<ModulePage title="FiveGuard" description="FiveGuard checks stay behind the documented bridge and ban history flow, and they are only treated as valid once the external source confirms them."><div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-slate-300">Supported verification states are tracked in the database. UNKNOWN is returned when a source does not verify a ban result.</div></ModulePage>} />
            <Route path="/dashboard/player-lookup" element={<ModulePage title="Player Lookup" description="Player and identifier lookups are persisted in the FiveM player tables and mapped through the bridge."><div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-slate-300">Records are tied to Discord, Steam, and license identifiers through the real FiveM bridge endpoints.</div></ModulePage>} />
            <Route path="/dashboard/settings" element={<SettingsEditor selectedGuildId={selectedGuildId} loadPath="/api/settings" title="General Settings" description="Save application settings to MariaDB for the selected guild and keep the dashboard aligned with the bot configuration." />} />
            <Route
              path="/dashboard/logs"
              element={
                <ModulePage title="Audit Logs" description="Moderation and bot activity are loaded from the Prisma audit logs table.">
                  <div className="space-y-3">
                    {(auditLogs.data?.logs ?? []).map((log) => (
                      <div key={log.id} className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-medium text-white">{log.type}</p>
                          <span className="text-xs text-slate-400">{new Date(log.time).toLocaleString()}</span>
                        </div>
                        <p className="mt-2 text-sm text-slate-300">{log.message}</p>
                      </div>
                    ))}
                  </div>
                </ModulePage>
              }
            />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJson<{ user: User }>('/auth/me')
      .then((payload) => {
        setUser(payload.user);
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-300">Loading ORIGIN...</div>;
  }

  if (!user) {
    return <LoginScreen />;
  }

  return <DashboardShell user={user} />;
}
